import { useReadyToInvoiceEligibility } from '@/hooks/tickets/useTicketBusinessState';
import { CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';

interface Props {
  ticketId: string;
}

/**
 * Phase 16 — Shows server-side eligibility for ready_to_invoice transition.
 *
 * The DB function `check_ready_to_invoice_eligibility` is the canonical
 * authority. This UI only mirrors its result.
 */
export function ReadyToInvoiceBlockersList({ ticketId }: Props) {
  const { data, isLoading } = useReadyToInvoiceEligibility(ticketId);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground p-3">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking eligibility…
      </div>
    );
  }
  if (!data) return null;

  if (data.ready) {
    return (
      <div className="flex items-center gap-2 text-sm rounded-md border bg-muted/40 p-3">
        <CheckCircle2 className="h-4 w-4 text-primary" />
        <span>Ready to invoice — all conditions met.</span>
      </div>
    );
  }

  return (
    <div className="rounded-md border border-warning/40 bg-warning/5 p-3">
      <div className="flex items-center gap-2 text-sm font-medium text-warning-foreground">
        <AlertCircle className="h-4 w-4" />
        Why isn't this ready to invoice?
      </div>
      <ul className="mt-2 space-y-1 ml-6 list-disc text-sm text-foreground">
        {data.blockers.map((b, i) => <li key={i}>{b}</li>)}
      </ul>
    </div>
  );
}
